import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'About', to: '/about' },
  { label: 'Testimonials', to: '/testimonials' },
];

const serviceLinks = [
  { label: 'All Services', to: '/services' },
  { label: '1-2-1 Personal Training', to: '/personal-training' },
  { label: 'Bespoke Online Coaching', to: '/online-training' },
  { label: 'Nutritional Coaching', to: '/nutritional-coaching' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 120);
  };

  // On hero (not scrolled) = white text. After scroll = dark text.
  const isDark = !scrolled && !mobileOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white shadow-[0_1px_12px_rgba(0,0,0,0.08)] py-3'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">

        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none flex-shrink-0">
          <span
            className={`text-2xl font-bold tracking-[0.14em] transition-colors duration-300 ${
              isDark ? 'text-white' : 'text-stone-900'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            LAURA MARTIN
          </span>
          <span
            className={`text-[9px] tracking-[0.4em] uppercase font-sans font-medium mt-0.5 transition-colors duration-300 ${
              isDark ? 'text-white/55' : 'text-stone-400'
            }`}
          >
            FITNESS &amp; COACHING
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">

          {/* Services dropdown — hover with delay to prevent gap-flicker */}
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              onClick={() => setServicesOpen((v) => !v)}
              className={`flex items-center gap-1.5 text-[11px] font-sans font-semibold tracking-[0.2em] uppercase transition-colors duration-300 cursor-pointer ${
                isDark
                  ? 'text-white/80 hover:text-white'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Services
              <i
                className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                  servicesOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Invisible bridge prevents mouse-gap closing */}
            {servicesOpen && (
              <div className="absolute top-full left-0 w-full h-3" />
            )}

            <div
              className={`absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 bg-white shadow-xl rounded-xl py-2 w-[240px] border border-stone-100 transition-all duration-200 origin-top ${
                servicesOpen
                  ? 'opacity-100 scale-100 pointer-events-auto translate-y-0'
                  : 'opacity-0 scale-95 pointer-events-none -translate-y-1'
              }`}
            >
              <div className="px-4 py-2 border-b border-stone-100 mb-1">
                <span className="text-[9px] tracking-[0.3em] uppercase font-semibold text-stone-400">
                  What I offer
                </span>
              </div>
              {serviceLinks.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  className="flex items-center gap-3 px-4 py-3 text-[11px] font-semibold tracking-wide text-stone-600 hover:text-stone-900 hover:bg-stone-50 transition-all group"
                  onClick={() => setServicesOpen(false)}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9a96e] flex-shrink-0 group-hover:scale-125 transition-transform" />
                  {s.label}
                </Link>
              ))}
            </div>
          </div>

          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `relative text-[11px] font-sans font-semibold tracking-[0.2em] uppercase transition-colors duration-300 group ${
                  isActive
                    ? isDark ? 'text-white' : 'text-stone-900'
                    : isDark
                    ? 'text-white/75 hover:text-white'
                    : 'text-stone-500 hover:text-stone-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{l.label}</span>
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-[#c9a96e] transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}

          <Link
            to="/contact"
            className={`ml-2 px-6 py-2.5 text-[10px] font-sans font-bold tracking-[0.25em] uppercase rounded-full transition-all duration-300 ${
              isDark
                ? 'border border-white/60 text-white hover:bg-white hover:text-stone-900'
                : 'bg-[#c9a96e] text-white hover:bg-[#a8863f] hover:shadow-md hover:shadow-[#c9a96e]/20'
            }`}
          >
            Work With Me
          </Link>
        </nav>

        {/* Mobile burger */}
        <button
          className="lg:hidden flex flex-col gap-[5px] w-7 items-end cursor-pointer p-1"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block h-[1.5px] w-6 transition-all duration-300 ${isDark ? 'bg-white' : 'bg-stone-900'} ${mobileOpen ? 'rotate-45 translate-y-[6.5px]' : ''}`} />
          <span className={`block h-[1.5px] w-4 transition-all duration-300 ${isDark ? 'bg-white' : 'bg-stone-900'} ${mobileOpen ? 'opacity-0' : ''}`} />
          <span className={`block h-[1.5px] w-6 transition-all duration-300 ${isDark ? 'bg-white' : 'bg-stone-900'} ${mobileOpen ? '-rotate-45 -translate-y-[6.5px]' : ''}`} />
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 top-0 bg-white z-40 transition-all duration-500 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col h-full px-8 pt-28 pb-10 overflow-y-auto">
          <nav className="flex flex-col gap-1">
            <button
              className="flex items-center justify-between py-4 border-b border-stone-100 text-stone-900 text-sm font-semibold tracking-[0.2em] uppercase w-full"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              Services
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 flex flex-col gap-1 pb-2">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="py-2.5 text-stone-500 text-sm font-medium border-b border-stone-50"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="py-4 border-b border-stone-100 text-stone-900 text-sm font-semibold tracking-[0.2em] uppercase"
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto pt-10 flex flex-col gap-4">
            <Link
              to="/contact"
              className="w-full text-center py-4 bg-[#c9a96e] text-white text-xs font-bold tracking-[0.25em] uppercase rounded-full hover:bg-[#a8863f] transition-all duration-300"
            >
              Work With Me
            </Link>
            <a
              href="https://www.instagram.com/wee_lola_fit"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center text-xs text-stone-400 tracking-widest uppercase"
            >
              @wee_lola_fit
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
